
import { motion } from "motion/react";
import AccordionIcon from '@/assets/svg/accordion-icon.svg';

import { ReduceMotionToggle, RhLink } from "@components";
import { getLinkData } from "@/core/utils";

import ExternalArrowIcon from "@/assets/svg/external-arrow.svg";

import classNames from "classnames/bind";
import styles from "./FooterColumns.module.scss";
import { useEffect, useRef, useState } from "react";
import { useViewSize } from "@/providers/ViewSizeContext";
import { useClientMount } from "@/providers/ClientMountProvider";
const cx = classNames.bind(styles);

type FooterColumn = FooterSettings["footerSettingsFields"]["footerColumns"][number] & {
  isLast: boolean;
  activeIndices: number[];
  handleHeaderClick: (index: number) => void;
  index: number;
};

type FooterLinkProps = FooterColumn["links"][number];

// Component to render individual footer links
const FooterLink = (props: FooterLinkProps) => {
  // If the link type is "external", it opens in a new tab and shows an external arrow icon
  const linkData = getLinkData(props);
  if (!linkData) return null;
  return (
    <RhLink className={cx("footer-link")}
      target={linkData?.target}
      href={linkData?.url}
      data-gtm-section="footer"
    >
      <span className={cx("text-m")} dangerouslySetInnerHTML={{ __html: linkData?.label }} />
      { linkData?.target === "_blank" && <ExternalArrowIcon /> }
    </RhLink>
  );
};

export default function FooterColumn({ links, activeIndices, label, handleHeaderClick, index, isLast }: FooterColumn) {
  const { isDesktop, isReady } = useViewSize();
  const { mounted } = useClientMount();

  // Use explicit collapsible mode check: anything under desktop breakpoint (< 1025px)
  const isCollapsibleMode = !isDesktop;

  const [tabIndex, setTabIndex] = useState(-1);
  const [expanded, setExpanded] = useState(false);
  const linksRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({ maxHeight: 0, opacity: 0 });
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousIsCollapsibleRef = useRef<boolean | null>(null);

  // Wait for client mount and viewport detection before setting initial state
  useEffect(() => {
    if (!mounted || !isReady) return;

    // Detect if collapsible mode changed (viewport mode switched)
    const modeChanged = previousIsCollapsibleRef.current !== null && previousIsCollapsibleRef.current !== isCollapsibleMode;
    previousIsCollapsibleRef.current = isCollapsibleMode;

    // Clear any pending timeout
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    // Use longer timeout to ensure everything settles
    // Mode changes happen immediately, resize stability gets 200ms
    const delay = modeChanged ? 0 : 200;

    resizeTimeoutRef.current = setTimeout(() => {
      setTabIndex(isCollapsibleMode ? -1 : 0);

      // Desktop mode: always expanded (no accordion)
      // Collapsible mode (mobile/tablet): only expanded if in activeIndices
      if (isCollapsibleMode) {
        setExpanded(activeIndices.includes(index));
      } else {
        setExpanded(true);
      }
    }, delay);

    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [isCollapsibleMode, activeIndices, index, mounted, isReady]);

  // Update maxHeight when expanded state or content changes
  useEffect(() => {
    if (!mounted || !isReady) return;

    let rafId: number;

    // Force immediate recalculation when transitioning between mobile/desktop
    const updateHeight = () => {
      if (expanded && linksRef.current) {
        const height = linksRef.current.scrollHeight;
        setStyle({ maxHeight: `${height}px`, opacity: 1 });
      } else {
        setStyle({ maxHeight: "0px", opacity: 0 });
      }
    };

    // Use requestAnimationFrame to ensure DOM has updated
    rafId = requestAnimationFrame(updateHeight);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [expanded, isCollapsibleMode, links.length, mounted, isReady]);

  // Don't render interactive elements until client is ready
  if (!mounted || !isReady) {
    return (
      <div className={cx("footer-column", { "is-collapsed": true })} key={index}>
        <div className={cx("footer-column__header")}>
          <p className={cx("footer-column__header__label")}>{label}</p>
        </div>
      </div>
    );
  }

   return (
      <motion.div className={cx("footer-column", { "is-collapsed": !expanded, "is-expanded": expanded })} key={index}>
        <button
          className={cx("footer-column__header")}
          tabIndex={tabIndex}
          role={ isCollapsibleMode ? "button" : "heading" }
          aria-expanded={expanded}
          aria-controls={`footer-column__links-${index}`}
          onClick={isCollapsibleMode ? () => handleHeaderClick(index) : undefined}
          onKeyDown={e => {
            if (isCollapsibleMode && (e.key === "Enter" || e.key === " ")) handleHeaderClick(index);
          }}
          data-gtm-section="footer"
          aria-level={ isCollapsibleMode ? undefined : 2}
        >
          <p className={cx("footer-column__header__label")}>{label}</p>
          <div className={cx("footer-column__header__icon")}>
            <AccordionIcon />
            <AccordionIcon className={cx("vertical")} />
          </div>
        </button>
        <div
          id={`footer-column__links-${index}`}
          className={cx("footer-column__links", { "is-collapsed": !expanded })}
          ref={linksRef}
          style={style}
          aria-hidden={!expanded}
        >
          {links.map((link, linkIndex) => (
            <FooterLink key={linkIndex} {...link} />
          ))}
        </div>
        {(isLast) &&
          <div className={cx("reduce-motion")}>
            <p className={cx("reduce-motion__label")} role="heading" aria-level={2}>Accessibility</p>
            <ReduceMotionToggle className={cx("reduce-motion__toggle")} dataGtmSection="footer" />
          </div>
        }
      </motion.div>
    );
}
